import { Node } from 'prosemirror-model';
import { NodeView } from 'prosemirror-view';

export type DOMNode = InstanceType<typeof window.Node>;
export type ViewMutationRecord = MutationRecord | { type: 'selection'; target: DOMNode };

/**
 * @public
 */
export class TableView implements NodeView {
  public dom: HTMLDivElement;
  public table: HTMLTableElement;
  public colgroup: HTMLTableColElement;
  public contentDOM: HTMLTableSectionElement;

  constructor(public node: Node, public defaultCellMinWidth: number) {
    this.dom = document.createElement('div');
    this.dom.className = 'tableWrapper';
    this.table = this.dom.appendChild(document.createElement('table'));
    this.table.style.setProperty('--default-cell-min-width', `${defaultCellMinWidth}px`);
    this.colgroup = this.table.appendChild(document.createElement('colgroup'));
    updateColumnsOnResize(node, this.colgroup, this.table, defaultCellMinWidth);
    this.contentDOM = this.table.appendChild(document.createElement('tbody'));
  }

  update(node: Node): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    updateColumnsOnResize(node, this.colgroup, this.table, this.defaultCellMinWidth);
    return true;
  }

  ignoreMutation(record: ViewMutationRecord): boolean {
    return record.type === 'attributes' && (record.target === this.table || this.colgroup.contains(record.target));
  }
}

/**
 * Custom updateColumnsOnResize to resize the sibling columns only
 *
 * @param node
 * @param colgroup
 * @param table
 * @param defaultCellMinWidth
 * @param overrideCol
 * @param overrideValue
 * @param adjustPrevious
 * @returns
 */
export function updateColumnsOnResize(
  node: Node,
  colgroup: HTMLTableColElement,
  table: HTMLTableElement,
  defaultCellMinWidth: number,
  overrideCol?: number,
  overrideValue?: number,
  adjustPrevious?: boolean,
): void {
  let totalWidth = 0;
  let nextDOM = colgroup.firstChild as HTMLElement;
  const row = node.firstChild;
  if (!row) return;

  const originalColWidths: number[] = [];

  // Step 1: Collect original widths
  for (let i = 0, col = 0; i < row.childCount; i++) {
    const { colspan, colwidth } = row.child(i).attrs as { colspan: number; colwidth?: number[] };
    for (let j = 0; j < colspan; j++, col++) {
      const width = colwidth?.[j] ?? defaultCellMinWidth;
      originalColWidths[col] = width;
    }
  }

  const newColWidths = [...originalColWidths];

  // Step 2: Apply resizing logic
  if (overrideCol !== undefined && overrideValue !== undefined) {
    const currentWidth = originalColWidths[overrideCol];
    let requestedDelta = overrideValue - currentWidth;
    const neighborCol = adjustPrevious ? overrideCol - 1 : overrideCol + 1;

    if (neighborCol >= 0 && neighborCol < newColWidths.length) {
      const neighborWidth = originalColWidths[neighborCol];

      // How much can the neighbor shrink?
      const maxShrinkFromNeighbor = neighborWidth - defaultCellMinWidth;

      // Clamp the requested delta based on neighbor's available shrink room
      const actualDelta = Math.max(
        -1 * (currentWidth - defaultCellMinWidth), // don't let dragged column shrink too much
        Math.min(requestedDelta, maxShrinkFromNeighbor),
      );

      // Apply balanced update
      newColWidths[overrideCol] = currentWidth + actualDelta;
      newColWidths[neighborCol] = neighborWidth - actualDelta;
    } else {
      // No neighbor — just clamp the override
      newColWidths[overrideCol] = Math.max(defaultCellMinWidth, overrideValue);
    }
  }

  // Step 3: Apply to <colgroup>
  for (let col = 0; col < newColWidths.length; col++) {
    const width = Math.max(newColWidths[col], defaultCellMinWidth);
    const cssWidth = `${Math.round(width)}px`;
    totalWidth += width;

    if (!nextDOM) {
      const colEl = document.createElement('col');
      colEl.style.width = cssWidth;
      colgroup.appendChild(colEl);
    } else {
      if (nextDOM.style.width !== cssWidth) {
        nextDOM.style.width = cssWidth;
      }
      nextDOM = nextDOM.nextSibling as HTMLElement;
    }
  }

  // Step 4: Remove leftover <col> elements
  while (nextDOM) {
    const after = nextDOM.nextSibling;
    nextDOM.parentNode?.removeChild(nextDOM);
    nextDOM = after as HTMLElement;
  }

  // Step 5: Keep table width fixed
  table.style.width = `${totalWidth}px`;
  table.style.minWidth = '';
}
